import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Building2,
  ChevronDown,
  ChevronRight,
  Flame,
} from "lucide-react";
import { usePortalAuth } from "../lib/portalAuth";
import { OW_API as ADMIN_API } from "../lib/operatorWalk";
import { useAdminBadges } from "../lib/adminBadges";

const SCORE_FILTERS = [
  { key: "all", label: "All", params: {} },
  { key: "hot", label: "Hot (<25)", params: { max_score: 24.9 } },
  { key: "warm", label: "Warm (25-37)", params: { min_score: 25, max_score: 37.9 } },
  { key: "cold", label: "Cold (38+)", params: { min_score: 38 } },
];

function formatDate(iso) {
  if (!iso) return "Not set";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function scoreColor(s) {
  if (s == null) return "text-ink-muted";
  if (s < 25) return "text-red-700";
  if (s < 38) return "text-amber-700";
  return "text-emerald-700";
}

function ScoreBar({ value }) {
  const pct = Math.max(2, (value / 10) * 100);
  return (
    <div className="bg-brand-100 h-1.5 w-full">
      <div className="bg-brand-700 h-1.5" style={{ width: `${pct}%` }} />
    </div>
  );
}

function DetailPanel({ id }) {
  const { authHeader } = usePortalAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${ADMIN_API}/admin/assessments/${id}`, { headers: authHeader })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => toast.error("Could not load detail"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, authHeader]);

  if (loading)
    return (
      <div className="px-6 py-10 flex items-center text-ink-muted">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading
      </div>
    );
  if (!data) return null;

  return (
    <div className="px-6 py-8 bg-white" data-testid={`assessment-detail-${id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-2">Lead</div>
          <div className="font-serif text-2xl text-brand-700 leading-tight">
            {data.lead?.name}
          </div>
          <div className="text-ink-soft text-sm mt-1 break-all">
            <a href={`mailto:${data.lead?.email}`} className="hover:text-brand-700">
              {data.lead?.email}
            </a>
          </div>
          <dl className="mt-5 text-sm space-y-2 text-ink-soft">
            <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-brand-400" /> {data.lead?.company}</div>
            <div className="flex items-center gap-2"><span className="text-ink-muted text-xs uppercase tracking-[0.18em]">Role</span> {data.lead?.role}</div>
            <div className="flex items-center gap-2"><span className="text-ink-muted text-xs uppercase tracking-[0.18em]">Locations</span> {data.lead?.locations}</div>
            <div className="flex items-center gap-2"><span className="text-ink-muted text-xs uppercase tracking-[0.18em]">Completed</span> {formatDate(data.completedAt)}</div>
          </dl>
          <a
            href={`mailto:${data.lead?.email}?subject=Re:%20your%20Operations%20Readiness%20results`}
            className="mt-6 inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 text-sm font-medium transition-colors"
            data-testid={`assessment-reply-${id}`}
          >
            <Mail className="w-4 h-4" /> Reply by Email
          </a>
        </div>
        <div className="lg:col-span-8">
          <div className="bg-brand-700 text-white p-6 mb-6 text-center">
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-300 mb-1">
              Overall
            </div>
            <div className="font-serif text-5xl leading-none">
              {data.total50}
              <span className="text-brand-300 text-2xl">/50</span>
            </div>
            <div className="text-brand-100/80 text-xs mt-2 max-w-md mx-auto">
              {data.overall_label}
            </div>
          </div>
          <ul className="space-y-5">
            {(data.categories || []).map((c) => {
              const detail = (data.detail || []).find((d) => d.key === c.key);
              return (
                <li key={c.key} className="border border-ink-line p-5">
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <div>
                      <div className="font-serif text-lg text-brand-700">{c.title}</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-brand-500 mt-0.5">
                        {c.label}
                      </div>
                    </div>
                    <div className="font-serif text-2xl text-brand-700">
                      {c.score10}
                      <span className="text-ink-muted text-sm">/10</span>
                    </div>
                  </div>
                  <ScoreBar value={c.score10} />
                  {detail && (
                    <ul className="mt-4 space-y-2 text-sm">
                      {detail.answers.map((qa, i) => (
                        <li key={i} className="flex items-start justify-between gap-3 text-ink-soft">
                          <span className="flex-1">{qa.question}</span>
                          <span className="font-serif text-brand-700 text-lg leading-none w-6 text-right">
                            {qa.score}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function AdminAssessments() {
  const { authHeader } = usePortalAuth();
  const { adjustBadge } = useAdminBadges();
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, hot: 0, warm: 0, cold: 0, unread: 0, in_progress: 0 });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const params = SCORE_FILTERS.find((f) => f.key === filter)?.params || {};
        const res = await axios.get(`${ADMIN_API}/admin/assessments`, {
          headers: authHeader,
          params,
        });
        setRows(res.data?.assessments || []);
        setCounts(res.data?.counts || counts);
      } catch {
        toast.error("Could not load assessments");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authHeader, filter]
  );

  useEffect(() => {
    load();
  }, [load]);

  const toggleRead = async (a) => {
    setBusyId(a.id);
    try {
      await axios.patch(
        `${ADMIN_API}/admin/assessments/${a.id}`,
        { read: !a.read },
        { headers: authHeader }
      );
      setRows((prev) => prev.map((r) => (r.id === a.id ? { ...r, read: !a.read } : r)));
      setCounts((c) => ({ ...c, unread: c.unread + (a.read ? 1 : -1) }));
      adjustBadge("assessments", a.read ? 1 : -1);
    } catch {
      toast.error("Failed to update");
    } finally {
      setBusyId(null);
    }
  };

  const deleteAssessment = async (a) => {
    setBusyId(a.id);
    try {
      await axios.delete(`${ADMIN_API}/admin/assessments/${a.id}`, { headers: authHeader });
      setRows((prev) => prev.filter((r) => r.id !== a.id));
      setCounts((c) => ({
        ...c,
        total: Math.max(0, c.total - 1),
        unread: c.unread - (a.read ? 0 : 1),
      }));
      if (!a.read) adjustBadge("assessments", -1);
      toast.success("Deleted.");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setBusyId(null);
      setConfirmId(null);
    }
  };

  return (
    <div data-testid="admin-assessments">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-ink-line border border-ink-line mb-8">
        {[
          { label: "Total", value: counts.total },
          { label: "Unread", value: counts.unread },
          { label: "Hot <25", value: counts.hot, accent: true },
          { label: "Warm 25-37", value: counts.warm },
          { label: "Cold 38+", value: counts.cold },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white p-5"
            data-testid={`assessment-stat-${s.label.toLowerCase().replace(/\W+/g, "-")}`}
          >
            <div className={`font-serif text-4xl leading-none ${s.accent ? "text-red-700" : "text-brand-700"}`}>
              {s.value}
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-ink-muted">
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-1 border-b border-ink-line">
          {SCORE_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 text-sm transition-colors border-b-2 -mb-px ${
                filter === f.key
                  ? "border-brand-700 text-brand-700 font-medium"
                  : "border-transparent text-ink-muted hover:text-brand-700"
              }`}
              data-testid={`assessment-filter-${f.key}`}
            >
              {f.key === "hot" && <Flame className="w-3.5 h-3.5 inline mr-1.5 text-red-600" />}
              {f.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => load(true)}
          className="inline-flex items-center gap-2 text-sm text-brand-700 hover:bg-brand-100 px-3 py-2 transition-colors"
          data-testid="admin-assessments-refresh"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center text-ink-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center border border-ink-line" data-testid="admin-assessments-empty">
          <div className="font-serif text-2xl text-brand-700">No assessments in this range.</div>
          <p className="mt-2 text-ink-muted">When operators complete the assessment, they show up here.</p>
        </div>
      ) : (
        <div className="border border-ink-line" data-testid="admin-assessments-list">
          {rows.map((a) => {
            const isOpen = expandedId === a.id;
            return (
              <div
                key={a.id}
                className={`border-b border-ink-line last:border-b-0 ${!a.read ? "bg-brand-50/40" : "bg-white"}`}
                data-testid={`assessment-row-${a.id}`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(isOpen ? null : a.id)}
                  className="w-full text-left grid grid-cols-12 gap-3 items-center px-5 py-4 hover:bg-brand-100/40 transition-colors"
                >
                  <div className="col-span-12 sm:col-span-4 min-w-0">
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown className="w-4 h-4 text-brand-700 shrink-0" /> : <ChevronRight className="w-4 h-4 text-brand-700 shrink-0" />}
                      <div className="font-medium text-brand-700 truncate">{a.lead?.name}</div>
                      {!a.read && (
                        <span className="inline-block w-2 h-2 rounded-full bg-brand-400 shrink-0" aria-label="unread" />
                      )}
                    </div>
                    <div className="text-xs text-ink-muted mt-1 truncate pl-6">{a.lead?.email}</div>
                  </div>
                  <div className="col-span-6 sm:col-span-3 min-w-0">
                    <div className="text-sm text-ink-soft truncate">{a.lead?.company}</div>
                    <div className="text-xs text-ink-muted">{a.lead?.locations} loc · {a.lead?.role}</div>
                  </div>
                  <div className="col-span-3 sm:col-span-2 text-right">
                    <div className={`font-serif text-2xl leading-none ${scoreColor(a.total50)}`}>
                      {a.total50}
                      <span className="text-ink-muted text-sm">/50</span>
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-3 text-right text-xs text-ink-muted">
                    {formatDate(a.completedAt)}
                  </div>
                </button>

                {isOpen && (
                  <>
                    <DetailPanel id={a.id} />
                    <div className="px-6 pb-6 bg-white flex flex-wrap items-center gap-3 border-t border-ink-line pt-4">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleRead(a); }}
                        disabled={busyId === a.id}
                        className="inline-flex items-center gap-2 border border-brand-700 text-brand-700 hover:bg-brand-100 px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
                        data-testid={`assessment-toggle-read-${a.id}`}
                      >
                        {a.read ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        Mark as {a.read ? "Unread" : "Read"}
                      </button>

                      {confirmId === a.id ? (
                        <div className="inline-flex items-center gap-2">
                          <span className="text-sm text-ink-soft">Are you sure?</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteAssessment(a); }}
                            disabled={busyId === a.id}
                            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
                            data-testid={`assessment-confirm-delete-${a.id}`}
                          >
                            {busyId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            Yes, delete
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmId(null); }}
                            className="text-sm text-ink-muted hover:text-brand-700 px-3 py-2"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmId(a.id); }}
                          className="inline-flex items-center gap-2 text-red-600 hover:bg-red-50 border border-red-200 px-4 py-2 text-sm font-medium transition-colors"
                          data-testid={`assessment-delete-${a.id}`}
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
